import config from '@/config'
import HttpClient from '@/api/http/client'
import store from 'store'
import ResponseError from '@/api/http/ResponseError'
import windowStore from '@/store/modules/window.store'

const http = new HttpClient(config.sabBaseUrl)

async function parseJsonBody(response) {
  try {
    const text = await response.text()
    if (!text) {
      return null
    }
    return JSON.parse(text)
  } catch {
    return null
  }
}

function tokenCarrierObjects(body) {
  if (!body || typeof body !== 'object') {
    return []
  }
  const out = []
  const push = o => {
    if (o && typeof o === 'object' && !out.includes(o)) {
      out.push(o)
    }
  }
  push(body)
  const data = body.data
  if (data && typeof data === 'object') {
    push(data)
    push(data.session)
    push(data.oauth)
    push(data.tokens)
    push(data.auth)
  }
  push(body.session)
  push(body.oauth)
  push(body.tokens)
  push(body.auth)
  return out
}

function findTokensInBody(body) {
  for (const obj of tokenCarrierObjects(body)) {
    const accessToken = obj.access_token ?? obj.accessToken
    const refreshToken = obj.refresh_token ?? obj.refreshToken
    if (
      typeof accessToken === 'string' &&
      typeof refreshToken === 'string' &&
      accessToken.length > 0 &&
      refreshToken.length > 0
    ) {
      return { accessToken, refreshToken }
    }
  }
  return null
}

function hasAccessAndRefreshTokens(body) {
  return findTokensInBody(body) != null
}

function extractTokens(body) {
  const found = findTokensInBody(body)
  if (!found) {
    return { accessToken: undefined, refreshToken: undefined }
  }
  return found
}

export default {
  async getCountryCode() {
    const country = store.get('country')
    if (country) {
      return country
    }

    try {
      const response = await http.get(`/api/v1/utils/country`).then(res => res.json())
      store.set('country', response.country)
      return response.country
    } catch {
      return 'US'
    }
  },
  async signIn(credentials) {
    const response = await http.post('/api/v1/sessions/login', credentials, true)

    if (response.ok) {
      const data = await response.json()
      http.setTokens(data.access_token, data.refresh_token)
      return
    }

    throw new ResponseError(response.status)
  },
  /**
   * Step 2 — complete login when a PIN is required (v2).
   * Body: { pincode, country_code, phone_number }
   */
  async signInWithPhone(credentials) {
    const response = await http.post(
      '/api/v2/sessions/login/phone',
      {
        pincode: String(credentials.pincode),
        country_code: Number(credentials.countryCode),
        phone_number: Number(credentials.phoneNumber)
      },
      true
    )

    const body = await parseJsonBody(response)

    if (response.ok) {
      if (hasAccessAndRefreshTokens(body)) {
        const { accessToken, refreshToken } = extractTokens(body)
        http.setTokens(accessToken, refreshToken)
        return
      }
      throw new ResponseError(response.status, body)
    }

    throw new ResponseError(response.status, body)
  },
  /**
   * Step 1 — request PIN / carrier verification (v2).
   * If the carrier does not require a PIN, the response may include tokens and the session is ready.
   * Returns { sessionEstablished: true } when tokens were returned; otherwise { sessionEstablished: false, body }.
   */
  async sendPhonePincode({ countryCode, phoneNumber }) {
    const response = await http.post(
      '/api/v2/sessions/send_pincode',
      {
        country_code: Number(countryCode),
        phone_number: Number(phoneNumber)
      },
      true
    )

    const body = await parseJsonBody(response)

    if (!response.ok) {
      throw new ResponseError(response.status, body)
    }

    if (hasAccessAndRefreshTokens(body)) {
      const { accessToken, refreshToken } = extractTokens(body)
      http.setTokens(accessToken, refreshToken)
      return { sessionEstablished: true }
    }

    return { sessionEstablished: false, body }
  },
  async signUp(credentials, extraInfo, authenticationPlatform, recaptchaToken, privacyPreferences) {
    extraInfo.emailLanguage = windowStore.state.language

    const response = await http.post('/api/v1/user', {
      email: credentials.email,
      password: credentials.password
    }, true)

    if (response.ok) {
      return
    }

    throw new ResponseError(response.status)
  },
  async subscriptionsStatus() {
    return http.get('/api/v1/subscriptions/status', true).then(data => data.json())
  },
  async signOut() {
    const response = await http.delete('/api/v1/sessions/logout', true)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      // No-op: `authentication/refreshToken` treats missing token as guest (no `tryAuthenticate`).
      return
    }
    const response = await http.post('/api/v1/sessions/refresh', {
      refresh_token: refreshToken
    }, true)
    if (response.ok) {
      const data = await response.json()
      http.setTokens(data.access_token, data.refresh_token)
      return
    }
    throw new ResponseError(response.status)
  },
  async getUser() {
    return http.get('/api/v1/user', true).then(data => data.json())
  },
  async verifyProfile() {
    return http.get('/api/v1/user/profiles', true).then(data => data.json())
  },
  async createChildProfile(childProfile) {
    const response = await http.post('/api/v1/user/profiles', childProfile, true)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async updateChildProfile(id, childProfile) {
    const response = await http.put(`/api/v1/user/profiles/${id}`, childProfile, true)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async removeChildProfile(id) {
    const response = await http.delete(`/api/v1/user/profiles/${id}`, true)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async getAllChildrenProfiles() {
    return http.get('/api/v1/user/profiles', true)
  },
  async changeEmail(email) {
    const response = await http.patch('/api/v1/user/change_email', { email }, true)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async changePassword(currentPassword, password) {
    const response = await http.patch('/api/v1/user/change_password', { current_password: currentPassword, password }, true)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async createVindiUser(email, skuId, card, billing) {
    const response = await http.post(`/api/v1/subscriptions/vindi/create_user`, {
      name: card.holderName,
      email: email,
      registry_code: billing.cpf,
      address: {
        street: billing.address,
        number: billing.number,
        additional_details: billing.additionalDetails,
        zipcode: billing.zipcode,
        neighborhood: billing.district,
        city: billing.city,
        state: billing.state,
        country: JSON.parse(localStorage.getItem('country'))
      }
    }, true)

    let body = null
    try { body = await response.json() } catch {}

    if (!response.ok) {
      return false
    }

    return body
  },
  async subscribeVindiUser(gatewayToken, vindiPlanId) {
    const response = await http.post(`/api/v1/subscriptions/vindi/subscribe`, {
      gateway_token: gatewayToken,
      vindi_plan_id: vindiPlanId
    }, true)

    let body = null
    try { body = await response.json() } catch {}

    if (!response.ok) {
      return false
    }

    return body
  },
  async cancelSubscription(subscriptionId) {
    const response = await http.post(`/api/v1/subscriptions/manage/${subscriptionId}/cancel`)

    if (!response.ok) {
      throw new ResponseError(response.status)
    }
  },
  async loginWithViuMsisdn(data, options = {}) {
    const url = new URL('/api/v1/partners/viu/safaricom/login', config.sabBaseUrl).toString()
    const headers = {
      'Content-Type': 'application/json'
    }
    if (options && typeof options.xApiKey === 'string' && options.xApiKey.trim()) {
      headers['x-api-key'] = options.xApiKey.trim()
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        msisdn: data?.msisdn == null ? '' : String(data.msisdn).trim()
      })
    })

    const body = await parseJsonBody(response)

    if (!response.ok) {
      throw new ResponseError(response.status, body)
    }

    if (hasAccessAndRefreshTokens(body)) {
      const { accessToken, refreshToken } = extractTokens(body)
      http.setTokens(accessToken, refreshToken)
    }

    return body
  }
}
